import { DEFAULT_AVATAR_URL } from '@constants/avatar'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { HeroIcon } from '@components/HeroIcon'
import { Modal } from '@components/Modal'
import { useCallback, useMemo } from 'react'
import { Avatar, XStack, YStack, Text, isWeb } from 'tamagui'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { useSignOut } from '@hooks/useUser'
import { useSession } from '@hooks/useSession'
import { openWebBrowser } from '@utils/openWebBrowser'
import { useDomain, useHasFeature } from '@hooks/useThemeParams'
import { MEMBERS_PAGE_MESSAGES } from '@constants/features'
import { router } from 'expo-router'

interface IMobileMenuProps {
  open: boolean
  onClose: () => void
}

interface IMenuOption {
  label: string
  icon: IconName
  url?: string
  appRoute?: string
  iconLibrary?: 'fal' | 'fas'
  isExternal?: boolean
}

// O app nativo não passa pelo nginx, que é quem aplica a flag
// members_page_messages no web. Sem checar a flag aqui, escola com a
// mensageria do members desligada continua com o acesso no menu — e no nativo
// não existe hero_theme para servir de fallback.
const MESSAGES_OPTION: IMenuOption = {
  label: 'Mensagens',
  icon: 'envelope',
  appRoute: '/channels',
}

export function MobileMenu({ open, onClose }: IMobileMenuProps) {
  const { user } = useSession()
  const { signOutMutate } = useSignOut()
  const { isPreview } = useAdminPreview()
  const hasMessagesPage = useHasFeature(MEMBERS_PAGE_MESSAGES)

  const menuOptions: IMenuOption[] = useMemo(() => {
    // Em preview só sobra o Sair: o produtor não tem perfil, pedidos,
    // mensagens nem certificados, e o AdminPreviewRouter devolveria ele para
    // /admin_view em qualquer dessas rotas.
    const forPreview = (options: IMenuOption[]) =>
      isPreview ? options.filter((option) => option.label === 'Sair') : options

    if (isWeb)
      return forPreview([
        { label: 'Meu Perfil', icon: 'user', url: `/users/${user?.id}/edit` },
        { label: 'Meus pedidos', icon: 'usd-circle', url: '/orders' },
        { label: 'Certificados', icon: 'user-graduate', url: '/certificates' },
        { label: 'Sair', icon: 'door-open' },
      ])

    return forPreview([
      {
        label: 'Política de Privacidade',
        iconLibrary: 'fas',
        icon: 'arrow-up-right-from-square',
        url: 'https://herospark.com/politicas-de-privacidade-2/',
        isExternal: true,
      },
      {
        label: 'Termos de Uso',
        iconLibrary: 'fas',
        icon: 'arrow-up-right-from-square',
        url: 'https://herospark.com/termos-de-uso-herospark/',
        isExternal: true,
      },
      {
        label: 'Editar perfil',
        icon: 'user',
        appRoute: `/users/${user?.id}/edit`,
      },
      ...(hasMessagesPage ? [MESSAGES_OPTION] : []),
      { label: 'Meus pedidos', icon: 'usd-circle', appRoute: '/orders' },
      { label: 'Sair', icon: 'door-open' },
    ])
  }, [user?.id, isPreview, hasMessagesPage])

  const hasAvatar =
    user?.coverImageUrl !== null &&
    user?.coverImageUrl !== undefined &&
    user?.coverImageUrl !== DEFAULT_AVATAR_URL

  const handleFAQ = useCallback(() => {
    openWebBrowser(
      'https://ajuda.herospark.com/hc/pt-br/articles/1750179550-como-encerrar-a-minha-conta-de-aluno-na-hero_spark',
    )
  }, [])

  const handleLogout = useCallback(() => {
    signOutMutate().finally(() => {
      onClose()
    })
  }, [signOutMutate, onClose])

  return (
    <Modal
      onClose={onClose}
      open={open}
      snapPointsMode="fit"
      shouldAdapt={true}
      whenShouldAdapt="md"
      disableDrag={true}
    >
      <YStack
        paddingHorizontal="$spacing-inset-sm"
        paddingVertical="$spacing-inset-md"
      >
        <YStack>
          <XStack
            gap="$spacing-inset-sm"
            alignItems="center"
            marginBottom="$spacing-inset-lg"
          >
            <Avatar
              circular
              size={40}
              borderColor="$icon-secondary"
              borderWidth={1}
            >
              {hasAvatar ? (
                <Avatar.Image src={user?.coverImageUrl} />
              ) : (
                <Avatar.Fallback
                  bc="$icon-secondary"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text
                    color="$text-on-accent"
                    fontSize="$font-size-sm"
                    textAlign="center"
                    fontWeight={700}
                  >
                    {user?.firstName?.[0]?.toUpperCase()}
                  </Text>
                </Avatar.Fallback>
              )}
            </Avatar>
            <YStack>
              <Text
                color="$text-primary"
                fontSize="$font-size-xs"
                ff="$mulishBold"
              >
                {user?.firstName} {user?.lastName}
              </Text>
              <Text
                color="$text-primary"
                fontSize="$font-size-xs"
                ff="$mulishSemiBold"
              >
                {user?.email}
              </Text>
            </YStack>
          </XStack>
          {menuOptions.map((option, index) => (
            <MenuOptions
              key={index}
              {...option}
              handleLogout={handleLogout}
              onClose={onClose}
            />
          ))}
        </YStack>

        <XStack gap="$spacing-inset-nano" alignItems="flex-start">
          <HeroIcon
            icon={['fal', 'question-circle']}
            color="$icon-secondary"
            size={16}
            style={{ marginTop: 3 }}
          />
          <XStack gap="$spacing-inset-nano" flexWrap="wrap">
            <Text
              color="$text-secondary"
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
              fontWeight={100}
            >
              Para apagar sua conta acesse nossa
            </Text>
            <Text
              color="$accent"
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
              fontWeight={100}
              textDecorationLine="underline"
              onPress={handleFAQ}
            >
              FAQ
            </Text>
            <Text
              color="$text-secondary"
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
              fontWeight={100}
            >
              para
            </Text>
            <Text
              color="$text-secondary"
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
              fontWeight={100}
            >
              mais instruções.
            </Text>
          </XStack>
        </XStack>
      </YStack>
    </Modal>
  )
}

interface IMenuOptionsProps {
  label: string
  icon: IconName
  url?: string
  appRoute?: string
  isExternal?: boolean
  iconLibrary?: 'fal' | 'fas'
  handleLogout?: () => void
  onClose?: () => void
}

export function MenuOptions({
  label,
  iconLibrary = 'fal',
  icon,
  url,
  appRoute,
  isExternal = false,
  handleLogout,
  onClose,
}: IMenuOptionsProps) {
  const { subdomainURL } = useDomain()

  const handlePress = () => {
    if (appRoute) {
      router.push(appRoute)
      onClose?.()
      return
    }

    if (!url) {
      handleLogout?.()
      return
    }

    if (isExternal) {
      openWebBrowser(url)
    } else {
      openWebBrowser(`${subdomainURL}${url}`)
    }
  }
  return (
    <XStack
      gap="$spacing-inset-nano"
      marginBottom="$spacing-inset-lg"
      onPress={handlePress}
      role="button"
    >
      <HeroIcon icon={[iconLibrary, icon]} color="$icon-secondary" size={20} />
      <Text color="$text-primary" ff="$mulishRegular" fontSize="$font-size-xs">
        {label}
      </Text>
    </XStack>
  )
}
